import React, { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import { ActivityFeedItem as ActivityFeedItemType } from '../../types/activity';
import { activityApi } from '../../services/activityApi';
import { ActivityFeedItem } from './ActivityFeedItem';
import { Button, IconButton } from '../ui/Button';
import { formatRelativeTime } from '../../utils/dateUtils';
import styles from './ActivityFeed.module.css';

interface ActivityFeedProps {
  limit?: number;
}

export interface ActivityFeedRef {
  refresh: (options?: { silent?: boolean }) => Promise<void>;
}

interface HideContactToast {
  type: 'hide_contact';
  contactId: string;
  contactName: string;
}
type ToastState = HideContactToast;

const buildSummaryStats = (activities: ActivityFeedItemType[]) => {
  const stats = {
    Contacts: 0,
    Tasks: 0,
    Calendar: 0,
    System: 0,
  };

  activities.forEach((activity) => {
    if (activity.contact || activity.groupedContact || activity.merge) {
      stats.Contacts += 1;
      return;
    }

    if (activity.task) {
      stats.Tasks += 1;
      return;
    }

    if (activity.calendarEvent || activity.birthday || activity.upcomingMeetings) {
      stats.Calendar += 1;
      return;
    }

    stats.System += 1;
  });

  return Object.entries(stats)
    .filter(([, value]) => value > 0)
    .map(([label, value]) => ({ label, value }));
};

export const ActivityFeed = forwardRef<ActivityFeedRef, ActivityFeedProps>(({ limit = 20 }, ref) => {
  const [activities, setActivities] = useState<ActivityFeedItemType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);
  const toastTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const fetchActivities = useCallback(async (options?: { silent?: boolean }) => {
    const isSilent = options?.silent ?? false;

    try {
      if (!isSilent) {
        setLoading(true);
        setError(null);
      }

      const response = await activityApi.getRecentActivity(limit);
      setActivities(response.activities);
      setError(null);
    } catch (err) {
      console.error('Error fetching activity feed:', err);

      if (!isSilent) {
        setError('Failed to load recent activity');
      }
    } finally {
      if (!isSilent) {
        setLoading(false);
      }
    }
  }, [limit]);

  // Expose refresh function via ref for parent components
  useImperativeHandle(ref, () => ({
    refresh: fetchActivities,
  }), [fetchActivities]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  // Cleanup toast timeout on unmount
  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
    };
  }, []);

  const showToast = (newToast: ToastState, duration: number = 5000) => {
    // Clear any existing toast timeout
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    
    setToast(newToast);
    
    // Auto-dismiss after duration
    toastTimeoutRef.current = setTimeout(() => {
      setToast(null);
    }, duration);
  };

  const handleHideContact = async (contactId: string, contactName: string) => {
    try {
      await activityApi.hideContact(contactId);
      
      // Remove activities for this contact from the list.
      setActivities(prev => prev.filter(a =>
        a.contact?.id !== contactId &&
        a.groupedContact?.id !== contactId &&
        a.birthday?.id !== contactId
      ));
      
      // Show undo toast
      showToast({ type: 'hide_contact', contactId, contactName });
    } catch (err) {
      console.error('Error hiding contact:', err);
    }
  };

  const handleUndo = async () => {
    if (!toast || toast.type !== 'hide_contact') return;
    
    try {
      await activityApi.unhideContact(toast.contactId);
      
      // Clear the toast
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
      setToast(null);
      
      // Refresh the feed to show the contact again
      fetchActivities();
    } catch (err) {
      console.error('Error unhiding contact:', err);
    }
  };

  const dismissToast = () => {
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    setToast(null);
  };

  const summaryStats = buildSummaryStats(activities);
  const latestActivityLabel = activities[0] ? formatRelativeTime(activities[0].timestamp) : null;
  const totalActivityLabel = `${activities.length} ${activities.length === 1 ? 'update' : 'updates'}`;

  const renderToast = () => {
    if (!toast) return null;

    return (
      <div className={styles.toast} role="alert">
        <span className={styles.toastMessage}>
          {`${toast.contactName} hidden from feed`}
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleUndo}
          className={styles.undoButton}
        >
          Undo
        </Button>
        <IconButton
          icon={<>&times;</>}
          aria-label="Dismiss"
          variant="ghost"
          size="sm"
          onClick={dismissToast}
          className={styles.toastDismiss}
        />
      </div>
    );
  };

  const renderHeader = () => (
    <div className={styles.header}>
      <div className={styles.headerCopy}>
        <div className={styles.kickerRow}>
          <span className={styles.kicker}>Homepage feed</span>
          {latestActivityLabel && (
            <span className={styles.headerTimestamp}>Latest {latestActivityLabel}</span>
          )}
        </div>

        <div className={styles.titleRow}>
          <h2 className={styles.title}>Recent activity</h2>
          {!loading && !error && (
            <span className={styles.countPill}>{totalActivityLabel}</span>
          )}
        </div>

        <p className={styles.subtitle}>
          Contacts, task progress, calendar movement, and system alerts in one stream.
        </p>
      </div>

      {summaryStats.length > 0 && (
        <div className={styles.summaryStats}>
          {summaryStats.map((stat) => (
            <div key={stat.label} className={styles.summaryStat}>
              <span className={styles.summaryValue}>{stat.value}</span>
              <span className={styles.summaryLabel}>{stat.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className={styles.container}>
        {renderHeader()}
        <div className={styles.loading}>
          <div className={styles.loadingSpinner}></div>
          <span>Loading activity...</span>
        </div>
        {renderToast()}
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        {renderHeader()}
        <div className={styles.error}>
          <span>{error}</span>
          <Button
            variant="primary"
            size="sm"
            onClick={() => {
              void fetchActivities();
            }}
            className={styles.retryButton}
          >
            Try again
          </Button>
        </div>
        {renderToast()}
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div className={styles.container}>
        {renderHeader()}
        <div className={styles.empty}>
          <span className={styles.emptyIcon}>📋</span>
          <p className={styles.emptyText}>No recent activity</p>
          <p className={styles.emptySubtext}>
            Your recent contact edits and task updates will appear here.
          </p>
        </div>
        {renderToast()}
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {renderHeader()}
      <div className={styles.feedList}>
        {activities.map((activity) => (
          <ActivityFeedItem
            key={activity.id}
            activity={activity}
            onHideContact={activity.contact || activity.groupedContact || activity.birthday ? handleHideContact : undefined}
          />
        ))}
      </div>
      
      {renderToast()}
    </div>
  );
});

ActivityFeed.displayName = 'ActivityFeed';

export default ActivityFeed;
